import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/context/AuthContext";
import { Loader2, ArrowLeft, Send, ExternalLink, FileText } from "lucide-react";
import { BRAND } from "@/lib/data";

export default function AdminClientDetail() {
  const { user_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  async function load() {
    const r = await http.get(`/admin/clients/${user_id}`);
    setData(r.data);
    setLoading(false);
  }
  useEffect(() => { load(); }, [user_id]);

  async function handleReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await http.post(`/admin/clients/${user_id}/messages`, { body: reply.trim() });
      setReply("");
      await load();
    } finally { setSending(false); }
  }

  if (loading) return <div className="flex items-center gap-2 text-navy/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!data) return <p className="text-navy/60">Not found.</p>;

  const { client: c, appointments, invoices, documents, messages } = data;

  return (
    <div className="space-y-8" data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </Link>
      <header className="card p-6 flex items-center gap-4">
        {c.picture ? <img src={c.picture} alt="" className="w-14 h-14 rounded-full" /> : <div className="w-14 h-14 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xl">{(c.name||"?")[0]}</div>}
        <div className="flex-1">
          <h1 className="text-3xl">{c.name}</h1>
          <div className="text-sm text-navy/60">{c.email}</div>
        </div>
        <div className="flex flex-col gap-2 text-right">
          <a
            href={BRAND.bookipiUrl}
            target="_blank" rel="noreferrer"
            data-testid="admin-bookipi-link"
            className="inline-flex items-center justify-end gap-1.5 text-xs text-navy/70 hover:text-gold transition"
          >
            <FileText className="w-3.5 h-3.5" /> Create proposal in Bookipi <ExternalLink className="w-3 h-3" />
          </a>
          {c.cdm_customer_id && (
            <a
              href="https://www.clientdisputemanager.com"
              target="_blank" rel="noreferrer"
              data-testid="admin-cdm-link"
              className="inline-flex items-center justify-end gap-1.5 text-xs text-navy/70 hover:text-gold transition"
            >
              <FileText className="w-3.5 h-3.5" /> Open in CDM <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </header>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-xl mb-4">Appointments</h3>
          {appointments.length === 0 ? <p className="text-sm text-navy/55">None yet.</p> : (
            <ul className="space-y-3 text-sm">
              {appointments.map((a) => <li key={a.appointment_id}><span className="text-navy">{a.title}</span> · <span className="text-navy/55">{a.starts_at}</span> · <span className="text-gold uppercase text-[10px]">{a.status}</span></li>)}
            </ul>
          )}
        </div>
        <div className="card p-6">
          <h3 className="text-xl mb-4">Invoices</h3>
          {invoices.length === 0 ? <p className="text-sm text-navy/55">None yet.</p> : (
            <ul className="space-y-3 text-sm">
              {invoices.map((i, k) => <li key={k}><span className="text-navy">{i.title || i.service_name}</span> · ${(i.amount_cents || 0) / 100} · <span className="text-gold uppercase text-[10px]">{i.status}</span></li>)}
            </ul>
          )}
        </div>
        <div className="card p-6">
          <h3 className="text-xl mb-4">Documents</h3>
          {documents.length === 0 ? <p className="text-sm text-navy/55">None yet.</p> : (
            <ul className="space-y-2 text-sm">
              {documents.map((d, k) => <li key={k} className="text-navy/80">{d.name}</li>)}
            </ul>
          )}
        </div>
        <div className="card p-6">
          <h3 className="text-xl mb-4">Recent Messages</h3>
          {messages.length === 0 ? <p className="text-sm text-navy/55">No messages.</p> : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
              {messages.slice(-6).map((m, k) => (
                <div key={k} className={`text-sm rounded-xl px-3 py-2 ${m.from === "client" ? "bg-navy/5 text-navy" : "bg-gold/10 text-navy border border-gold/20"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-0.5">{m.from === "client" ? c.name : (m.from_name || "Team")}</div>
                  {m.body}
                </div>
              ))}
            </div>
          )}
          <form onSubmit={handleReply} className="mt-4 flex gap-2" data-testid="admin-reply-form">
            <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="input-field flex-1 text-sm" data-testid="admin-reply-input" />
            <button disabled={sending || !reply.trim()} className="btn-primary" data-testid="admin-reply-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3 h-3" /> Send</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
